<script setup lang="ts">
import {
  tpgIconChevronRight,
  tpgIconNoVisibility,
  tpgIconPublics,
  tpgIconVisibility,
} from '@tpg/grammar-web-icons';

import {
  GrammarBadge,
  GrammarCard,
  GrammarIcon,
  GrammarQuaternaryButton,
  GrammarText,
} from '@nora-monorepo/grammar-ui';
import { truncateText } from '@nora-monorepo/ui-shared';

import { useMapStore } from '#nora-ui/map/stores/map-store';
import { useOperationStore } from '#nora-ui/stores/operation-store';

const props = defineProps<{
  mapId: string;
}>();

const emits = defineEmits<{
  (event: 'selectOperation', operationId: string, showDetails: boolean): void;
}>();

const { mapId } = props;

const mapStore = useMapStore();
const operationStore = useOperationStore();

const map = mapStore.getMapByIdOrThrow(mapId);

const MAX_OPERATION_NAME_LENGTH = 25;

function isShown(operationId: string): boolean {
  return map.ref.isOperationVisible(operationId);
}

function toggleVisibility(operationId: string) {
  if (isShown(operationId)) {
    map.hideOperation(operationId);
  }
  else {
    void map.showOperation(operationId);
  }
}

function selectOperation(operationId: string, showDetails: boolean) {
  emits('selectOperation', operationId, showDetails);
}

/**
 * Whether the given operation owns the active draw layer.
 */
function hasActiveLayer(operationId: string): boolean {
  const activeDrawLayer = map.state.draw.activeDrawLayer.value;

  return activeDrawLayer !== undefined && activeDrawLayer.operationId === operationId;
}

/**
 * The "Active layer: …" line for the operation that owns the active draw layer.
 */
function activeLayerLabel(operationId: string): string | undefined {
  if (!hasActiveLayer(operationId)) {
    return undefined;
  }

  return `Active layer: ${map.state.draw.activeDrawLayer.value?.name.slice(0, 20)}`;
}
</script>

<template>
  <div>
    <template v-if="operationStore.operations.length > 0">
      <div class="flex flex-col gap-2">
        <div
          v-for="operation in operationStore.operations"
          :key="operation.id"
          class="cursor-pointer"
          @click="selectOperation(operation.id, false)"
        >
          <GrammarCard
            :current-indicator="hasActiveLayer(operation.id)"
            :no-background="!isShown(operation.id)"
          >
            <template #header-leading-buttons>
              <GrammarIcon
                class="pl-2 pr-3 pt-2"
                :icon-template="tpgIconPublics"
              />
            </template>

            <template #header-trailing-buttons>
              <span @click.stop>
                <GrammarQuaternaryButton
                  :icon-template="isShown(operation.id) ? tpgIconVisibility : tpgIconNoVisibility"
                  @click="toggleVisibility(operation.id)"
                />
              </span>
              <span @click.stop>
                <GrammarQuaternaryButton
                  :icon-template="tpgIconChevronRight"
                  @click="selectOperation(operation.id, true)"
                />
              </span>
            </template>

            <template #header-stuff>
              <div
                class="flex flex-col"
              >
                <GrammarText :text="truncateText(operation.name, MAX_OPERATION_NAME_LENGTH)" />
                <!-- Negative margins are because the paddings are baked into the GrammarBadge, and we can not overrule for now -->
                <GrammarBadge
                  v-if="hasActiveLayer(operation.id)"
                  class="-mt-0.5 -ml-1"
                  type="dynamic"
                  emphasis="no-background"
                  :label="activeLayerLabel(operation.id)"
                />
              </div>
            </template>
          </GrammarCard>
        </div>
      </div>
    </template>
    <template v-else>
      <div class="flex flex-col items-center pt-5">
        <GrammarBadge
          label="No available operations"
          emphasis="no-background"
        />
        <GrammarBadge
          label="Accept operation invitations to have them listed here"
          emphasis="no-background"
        />
      </div>
    </template>
  </div>
</template>
